import React, { useState } from "react";

import styles from "./MarketingPage.module.scss";

// Admin-only: the Achtung Royale marketing shelf. Screenshots we actually want
// to hand out, the brand files, and the copy that is already live in the
// product, all in one place so a store listing or a post can be assembled
// without going hunting.
//
// The images are real files in public/marketing/, so every path here is also
// the public URL (achtungroyale.io/marketing/<file>). Add a shot by dropping a
// JPEG in that folder and adding a row to SHOTS.
//
// Every shot and brand file carries a Copy URL button with the ABSOLUTE
// production URL, because that is what a forum that embeds images by URL
// (SweClockers, Flashback, most phpBB boards) asks for. Deliberately not
// window.location.origin: from a dev server that yields a localhost URL that
// works for nobody but you.

const SITE = "https://achtungroyale.io";

interface Shot {
  file: string;
  w: number;
  h: number;
  title: string;
  note: string;
  lead?: boolean;
}

// Long side capped at 1800 px, JPEG quality 80. Big enough for a store listing
// or a press mail, small enough that the whole folder is about 2 MB.
const SHOTS: Shot[] = [
  {
    file: "royale-late-round.jpg", w: 1800, h: 1052, lead: true,
    title: "Royale, 11 of 30 left",
    note: "October 2026, current look. A 30-player Royale late in round 1: the arena packed with trails. Lead with this one.",
  },
  {
    file: "royale-mid-round.jpg", w: 1800, h: 1052,
    title: "Royale, mid-round",
    note: "October 2026. The same Royale a few seconds earlier, trails spreading out over the arena.",
  },
  {
    file: "royale-start.jpg", w: 1800, h: 1052,
    title: "Royale, the start",
    note: "October 2026. 30 worms with their direction arrows, just before GO.",
  },
  {
    file: "royale-wall.jpg", w: 1800, h: 1052,
    title: "Royale, the player wall",
    note: "October 2026. The ROYALE screen with every player's name before the round.",
  },
  {
    file: "ranked-1v1-missile.jpg", w: 1800, h: 1130,
    title: "Ranked 1 vs 1, power-ups",
    note: "October 2026. A ranked duel with a missile in the air and power-ups on the field.",
  },
  {
    file: "ranked-1v1-vs.jpg", w: 1800, h: 1130,
    title: "Ranked 1 vs 1, VS banner",
    note: "October 2026. The VS banner at the start of a ranked duel.",
  },
  {
    file: "arena-royale-chaos.jpg", w: 1800, h: 1128,
    title: "Late in the round, three left",
    note: "Round 4, three players alive of eight, and the arena almost closed up. The strongest single image we have: lead with this one.",
  },
  {
    file: "arena-royale-chaos-wide.jpg", w: 1800, h: 1135,
    title: "The same round, a little earlier",
    note: "Six alive instead of three, so there is more open space to put text over. Close to a duplicate of the lead shot: use one or the other, not both.",
  },
  {
    file: "home-page.jpg", w: 1800, h: 1146,
    title: "Home, signed in",
    note: "October 2026, current look. The live Play Now round, the big LET'S GO button, the Royale countdown, ranked and game rooms.",
  },
  {
    file: "home-phone.jpg", w: 1800, h: 796,
    title: "Home on a phone",
    note: "October 2026. The landscape phone layout: the live round behind, LET'S GO, Royale, Ranked and Rooms.",
  },
  {
    file: "royale-queue.jpg", w: 1800, h: 1054,
    title: "Royale: filling up",
    note: "October 2026. Players joining the next Royale, 10 of 30, with the countdown and Play while you wait.",
  },
  {
    file: "game-room.jpg", w: 1800, h: 1008,
    title: "Game room",
    note: "October 2026. Hosting a room: map, winning condition, speed, mode, power-ups, the invite QR code and chat.",
  },
  {
    file: "match-over.jpg", w: 1800, h: 1036,
    title: "Match over",
    note: "The winner on the podium, the final standings with everyone's rating change, and the match stats. The one to use when the point is that this is ranked and it counts.",
  },
  {
    file: "leaderboard.jpg", w: 1800, h: 1128,
    title: "The ranked ladder",
    note: "Proof there is a real ladder behind the chaos. Pairs well with the profile shot.",
  },
  {
    file: "profile-rating.jpg", w: 1800, h: 1358,
    title: "A player profile",
    note: "October 2026. Royale wins and their egg rewards, recent Royales, the rating and its graph.",
  },
  {
    file: "shop-celebrations.jpg", w: 1800, h: 1276,
    title: "The store: victory celebrations",
    note: "The whole shop is cosmetic, which is the point worth making every time it is shown.",
  },
];

// Gameplay video, 1920 x 1080 at 60 fps, H.264 MP4 (what Reddit and most
// stores take as an upload). Cut from real screen recordings with ffmpeg.
const VIDEOS: Shot[] = [
  {
    file: "royale-and-ranked.mp4", w: 1920, h: 1080, lead: true,
    title: "Royale, then a ranked 1 vs 1",
    note: "53 seconds with the game's music and sounds: the start page, joining the Royale, the wall of 30 names, the countdown and the round, a fade to black, then a ranked 1 vs 1 from the VS banner to the round score. The one to post.",
  },
  {
    file: "royale-30-players.mp4", w: 1920, h: 1080,
    title: "Royale, 30 players",
    note: "43 seconds with music and sounds: from the start page into a 30-player Royale, down to the last three.",
  },
  {
    file: "ranked-1v1.mp4", w: 1920, h: 1080,
    title: "Ranked 1 vs 1",
    note: "25 seconds with music and sounds: the VS banner, the countdown, one round with power-ups, and the 0-1 score.",
  },
  {
    file: "crazygames-preview-1920x1080.mp4", w: 1920, h: 1080,
    title: "CrazyGames preview, landscape",
    note: "18 seconds, no sound (their rule): the landscape cover, a fade, then a 30-player Royale from the countdown to a full arena. Cut from royale-30-players.mp4.",
  },
  {
    file: "crazygames-preview-1080x1620.mp4", w: 1080, h: 1620,
    title: "CrazyGames preview, portrait",
    note: "The same 18 seconds at 1080 x 1620 (2:3), the arena cropped upright, opening on the portrait cover.",
  },
];

interface Asset {
  path: string;
  title: string;
  note: string;
  dark?: boolean;
  // a portrait image: a taller thumbnail, filled edge to edge
  portrait?: boolean;
}

const ASSETS: Asset[] = [
  { path: "/logo-mark.svg", title: "Logo mark", note: "The A drawn by two worms. Transparent, crops tight, scales to anything.", dark: true },
  { path: "/favicon.svg", title: "Square icon", note: "The same mark on the brand background. Use where a square is required." },
  { path: "/marketing/cover-3x4.jpg", title: "Cover (3:4)", portrait: true, note: "1200 x 1600 game cover: IGDB and other game databases, store tiles. The board of a real eight-player round under the logo and \"Last worm wins\"." },
  { path: "/marketing/banner-728x90.png", title: "Banner 728 x 90", note: "The logo, the name and \"Up to 30 players online, free browser game\". The banner size vibetoplist.com requires (and the classic leaderboard ad size). Source: docs/marketing/banner-728x90.html." },
  { path: "/marketing/title-card.png", title: "Title card (16:9)", note: "1776 x 1000: the logo and name over worm trails, a stand-in title screen. Made for DavidKBD's portfolio (he wrote our music); there is a 444 x 250 copy at /marketing/title-card-444x250.png. Source: docs/marketing/title-card.html." },
  { path: "/marketing/cover-630x500.png", title: "Cover 630 x 500", note: "The title card fitted to 630 x 500, its top and bottom edges faded into the background. The cover size itch.io recommends (the game page and every listing on itch)." },
  { path: "/marketing/crazygames-cover-1920x1080.png", title: "CrazyGames cover 16:9", note: "1920 x 1080: the logo and name over worm trails, nothing else (CrazyGames allows only the title on a cover). Source: docs/marketing/covers.html." },
  { path: "/marketing/crazygames-cover-800x1200.png", title: "CrazyGames cover 2:3", portrait: true, note: "800 x 1200: the same cover upright, the name on two lines." },
  { path: "/marketing/crazygames-cover-800x800.png", title: "CrazyGames cover 1:1", note: "800 x 800: the same cover, square." },
  { path: "/marketing/facebook-cover-1640x624.png", title: "Facebook cover", note: "1640 x 624: the same cover, wide. A phone shows only the middle 1110 px and the profile picture covers the bottom left, so the title stays in the middle." },
  { path: "/marketing/profile-picture-1080x1080.png", title: "Profile picture", note: "1080 x 1080: the logo on the dark background, with room for a round crop (Facebook)." },
  { path: "/og-image.png", title: "Social card", note: "1200 x 630, what every link preview shows. Deliberately text free." },
  { path: "/pwa-512x512.png", title: "App icon", note: "512 x 512. Also the logo in the site's structured data." },
];

interface Snippet {
  id: string;
  label: string;
  note: string;
  text: string;
}

// The copy that is already live. index.html is the source of truth for the meta
// tags: if one changes there, change it here too.
const SNIPPETS: Snippet[] = [
  {
    id: "oneline",
    label: "One line",
    note: "For a directory entry or a byline.",
    text: "The classic Achtung, die Kurve! reborn for online play, free and never pay-to-win.",
  },
  {
    id: "short",
    label: "Short description",
    note: "Live as the page's og:description.",
    text: "A modern, free take on Achtung, die Kurve! Online games with up to 30 players, or local play on one computer. Be the last worm alive.",
  },
  {
    id: "long",
    label: "Long description",
    note: "Live as the page's meta description. The one to paste into a store listing.",
    text: "Play Achtung, die Kurve! free in your browser. Online multiplayer with up to 30 players, a ranked ladder and power-ups, plus local play with friends on one computer. Free to play and never pay-to-win.",
  },
  {
    id: "studio",
    label: "The studio line",
    note: "Who made it, when someone asks.",
    text: "Achtung Royale is made by Skill Check Games, a Swedish studio of two, Mathias and Erik. The engine is our own, written from scratch.",
  },
];

const LINKS: { label: string; url: string }[] = [
  { label: "The game", url: "https://achtungroyale.io" },
  { label: "The studio", url: "https://skillcheckgames.com" },
  { label: "Discord (game)", url: "https://discord.gg/uqMGcrcJWN" },
  { label: "VibeTopList listing", url: "https://vibetoplist.com/listing/achtung-royale" },
  { label: "YouTube: Royale and ranked", url: "https://youtu.be/BNGQvjWfpUo" },
  { label: "Facebook", url: "https://www.facebook.com/achtungroyale" },
  { label: "Discord (studio)", url: "https://discord.gg/ZyXZP9k6f5" },
  { label: "Support us", url: "https://ko-fi.com/achtungroyale" },
];

function CopyButton({ text, id }: { text: string; id: string }) {
  const [state, setState] = useState<"idle" | "done" | "failed">("idle");
  return (
    <button
      className={styles.copy}
      data-state={state}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setState("done");
        } catch {
          setState("failed");
        }
        setTimeout(() => setState("idle"), 1800);
      }}
      aria-label={`Copy the ${id} text`}
    >
      {state === "done" ? "Copied" : state === "failed" ? "Select it instead" : "Copy"}
    </button>
  );
}

// The absolute URL, selectable, next to a button that copies it. Both matter:
// the button for the common case, the visible text for when the clipboard API
// is blocked and you have to select it by hand. Download saves the file itself
// (same-origin path, so the download attribute is honoured).
function UrlRow({ url, label, path }: { url: string; label: string; path: string }) {
  return (
    <div className={styles.urlRow}>
      <code className={styles.url}>{url}</code>
      <CopyButton text={url} id={`${label} URL`} />
      <a className={styles.copy} href={path} download={path.split("/").pop()} aria-label={`Download ${label}`}>Download</a>
    </div>
  );
}

export default function MarketingPage() {
  return (
    <div className={styles.page}>
      <p className={styles.lead}>
        Everything needed to put Achtung Royale in front of someone. The screenshots are
        real files under <code>apps/client/public/marketing/</code>, so each one is also
        live at <code>achtungroyale.io/marketing/&lt;file&gt;</code>. Every image below has
        its full public URL under it: copy that straight into a mail, a store listing, or a
        forum that embeds images by URL rather than by upload. A file only goes live once it
        is committed and deployed, so a shot added a minute ago will 404 until then.
      </p>

      <h2 className={styles.sectionTitle}>Screenshots</h2>
      <div className={styles.shots}>
        {SHOTS.map((s) => (
          <figure key={s.file} className={styles.shot} data-lead={s.lead ? "true" : undefined}>
            <a href={`/marketing/${s.file}`} target="_blank" rel="noreferrer" className={styles.shotLink}>
              <img src={`/marketing/${s.file}`} alt={s.title} loading="lazy" width={s.w} height={s.h} />
            </a>
            <figcaption>
              <h3>
                {s.title}
                {s.lead && <span className={styles.badge}>Lead image</span>}
              </h3>
              <p>{s.note}</p>
              <p className={styles.meta}>{s.w} x {s.h}</p>
              <UrlRow url={`${SITE}/marketing/${s.file}`} label={s.title} path={`/marketing/${s.file}`} />
            </figcaption>
          </figure>
        ))}
      </div>

      <h2 className={styles.sectionTitle}>Video</h2>
      <div className={styles.shots}>
        {VIDEOS.map((v) => (
          <figure key={v.file} className={styles.shot} data-lead={v.lead ? "true" : undefined}>
            <video className={styles.video} src={`/marketing/${v.file}`} controls muted loop playsInline preload="metadata" width={v.w} height={v.h} />
            <figcaption>
              <h3>
                {v.title}
                {v.lead && <span className={styles.badge}>Lead video</span>}
              </h3>
              <p>{v.note}</p>
              <p className={styles.meta}>{v.w} x {v.h}, MP4</p>
              <UrlRow url={`${SITE}/marketing/${v.file}`} label={v.title} path={`/marketing/${v.file}`} />
            </figcaption>
          </figure>
        ))}
      </div>

      <h2 className={styles.sectionTitle}>Brand files</h2>
      <div className={styles.assets}>
        {ASSETS.map((a) => (
          <div key={a.path} className={styles.asset}>
            <a className={styles.assetArt} data-dark={a.dark ? "true" : undefined} data-portrait={a.portrait ? "true" : undefined} href={a.path} target="_blank" rel="noopener noreferrer" title="Open full size">
              <img src={a.path} alt={a.title} loading="lazy" />
            </a>
            <div className={styles.assetText}>
              <h3>{a.title}</h3>
              <p>{a.note}</p>
              <UrlRow url={`${SITE}${a.path}`} label={a.title} path={a.path} />
            </div>
          </div>
        ))}
      </div>

      <h2 className={styles.sectionTitle}>Copy you can paste</h2>
      <div className={styles.snippets}>
        {SNIPPETS.map((s) => (
          <div key={s.id} className={styles.snippet}>
            <div className={styles.snippetHead}>
              <h3>{s.label}</h3>
              <CopyButton text={s.text} id={s.label.toLowerCase()} />
            </div>
            <p className={styles.snippetNote}>{s.note}</p>
            <p className={styles.snippetText}>{s.text}</p>
          </div>
        ))}
      </div>

      <h2 className={styles.sectionTitle}>Links</h2>
      <ul className={styles.links}>
        {LINKS.map((l) => (
          <li key={l.url}>
            <span>{l.label}</span>
            <a href={l.url} target="_blank" rel="noreferrer">{l.url}</a>
          </li>
        ))}
      </ul>
    </div>
  );
}
